/**
 * src/modules/types.ts — module-tier contract for AWENI native modules.
 *
 * This file extends the minimal `AweniModule` interface in `src/types/index.ts`
 * (which all existing modules implement: `{ mount, unmount }`) with the
 * additional pieces a module needs to **show a preview card on the AWENI home
 * "Today" zone** and to participate in the kill-the-iframe migration.
 *
 * Read this with `services/dispatch/strategic/ST3_AWENI_integration_spec.md`
 * and `docs/MODULE-INTEGRATION.md` open. The decision is locked:
 *
 *   AWENI mounts Hisni & Yawmi as native Vite modules in the same bundle.
 *   No iframes. Standalone /hisni/ and /yawmi/ URLs serve the SAME bundle
 *   with `?shell=minimal` to strip AWENI chrome (TopBar / BottomNav).
 *
 * Why a separate file:
 *   - `src/types/index.ts` is a global types barrel (~120 lines). Adding
 *     all the module-integration types there bloats it and tangles
 *     module-specific concerns into the global API.
 *   - Co-locating `types.ts` next to module code lets us keep module
 *     contracts DI-able and tree-shakable.
 *   - Existing modules keep importing `AweniModule` from `'../../types'`
 *     unchanged — this file is purely additive.
 *
 * Backward compatibility:
 *   Existing modules ({Games,Blog,Community,Family,...}Module) implement
 *   the minimal `AweniModule` shape in `src/types/index.ts`. They do NOT
 *   need to migrate — the home renders them via the legacy iframe/import
 *   path. Only modules opting into the home "Today" zone implement the
 *   `PreviewableAweniModule` interface below.
 */

import type { AweniContext, AweniModule, Lang } from '../types';

// ---------------------------------------------------------------------------
// PreviewSnapshot — the cached payload a previewable module surfaces on the
// home "Today" zone. MUST be cheap to compute (synchronous-friendly): the
// home calls `preview()` only inside a `requestIdleCallback` after first
// paint, and the synchronous render uses the LAST CACHED snapshot.
//
// Per `AWENI/CLAUDE.md` Home Page Anti-Regression Rule #1: nothing the
// home renders synchronously may await a third-party API. Cached
// snapshots in localStorage / IndexedDB satisfy this.
// ---------------------------------------------------------------------------
export interface PreviewSnapshot {
  /** A short headline metric. Renders top-left of the card. */
  primaryMetric: { label: string; value: string };
  /** Optional secondary metric. Renders below primary. */
  secondaryMetric?: { label: string; value: string };
  /** CTA button label — i18n-resolved. e.g. "Open Hisni" / "Open Yawmi". */
  ctaLabel: string;
  /** Hash route to navigate when the card or CTA is tapped. Must start
   *  with `#` and use the same routing the rest of the app uses. */
  ctaRoute: string;
  /** Icon ID matching `src/modules/home/icons.ts` (`mosque`, `check-mark`, ...). */
  iconName: string;
  /** Localized aria-label for the whole card (mobile a11y). */
  ariaLabel: string;
  /** Visual hint — drives subtle background tint per data-state CSS. */
  bgVariant?: 'neutral' | 'success' | 'attention';
  /** When the snapshot itself failed to compute, show this graceful
   *  fallback instead of hiding the card or showing a broken state.
   *  errorState OVERRIDES every other field except ctaRoute (so the
   *  user can still tap "Open" even if metrics failed). */
  errorState?: { message: string; recoveryRoute?: string };
  /** When the snapshot was computed (ISO datetime). Cards staler than
   *  24 h are flagged in dev mode. */
  computedAt?: string;
}

// ---------------------------------------------------------------------------
// MountedInstance — opaque handle returned by a previewable module's
// mount(). The home uses it to call destroy() when the user navigates away.
// ---------------------------------------------------------------------------
export interface MountedInstance {
  /** Unique per mount — the home uses this to dedupe rapid mount/unmount cycles. */
  readonly mountId: string;
  /** True when destroy() has been called. */
  readonly isDestroyed: boolean;
}

// ---------------------------------------------------------------------------
// ModuleRoute — declarative route ownership. A previewable module declares
// which hash routes it owns; the router uses this to dispatch.
// ---------------------------------------------------------------------------
export interface ModuleRoute {
  /** Hash path (without leading `#`). e.g. `/hisni`, `/hisni/quran/memorize`. */
  path: string;
  /** Optional human-readable label for breadcrumbs / nav. */
  label?: Partial<Record<Lang, string>>;
  /** When true, this route is the module's primary entry. The home uses
   *  this for the preview card's CTA. */
  primary?: boolean;
}

// ---------------------------------------------------------------------------
// PreviewableAweniModule — the new contract.
//
// Strict superset of the legacy `AweniModule` (mount/unmount), so a module
// that implements this also satisfies the legacy interface.
// ---------------------------------------------------------------------------
export interface PreviewableAweniModule extends AweniModule {
  /** Stable module ID — matches the `id` in `doors.config.json`. */
  readonly id: string;
  /** Routes this module owns. The router (`src/router.ts`) reads this
   *  on registration to populate MODULE_MAP. */
  readonly routes: ModuleRoute[];
  /**
   * Compute the current `PreviewSnapshot`. MUST resolve quickly (target
   * <50 ms). Implementations should:
   *   1. Read cached state (localStorage / IDB) — sync.
   *   2. If stale, kick off a refresh in the background and return the
   *      stale snapshot now (stale-while-revalidate).
   *   3. NEVER await network calls in this method.
   *
   * Returning `errorState` is preferred over throwing: the home is robust
   * to errorState but treats throws as graceful degrade fallback.
   */
  preview(context: AweniContext): Promise<PreviewSnapshot>;
  /**
   * Destroy a mounted instance. Called by the shell when the user
   * navigates to a different module. Must release event listeners,
   * intervals, IDB cursors, etc. Idempotent: safe to call twice.
   */
  destroy(instance: MountedInstance): void;
}

// ---------------------------------------------------------------------------
// Type guard — runtime check whether a module implements the previewable
// contract. Used by the home to decide whether to render a preview card.
// ---------------------------------------------------------------------------
export function isPreviewable(m: unknown): m is PreviewableAweniModule {
  return (
    typeof m === 'object' && m !== null &&
    typeof (m as Record<string, unknown>).id === 'string' &&
    Array.isArray((m as Record<string, unknown>).routes) &&
    typeof (m as Record<string, unknown>).preview === 'function' &&
    typeof (m as Record<string, unknown>).destroy === 'function' &&
    typeof (m as AweniModule).mount === 'function'
  );
}

// ---------------------------------------------------------------------------
// PreviewSnapshot defaults — the home falls back to these when a previewable
// module's `preview()` rejects or times out (>200 ms). Each module SHOULD
// provide a real snapshot; this is a safety net so the home never breaks.
// ---------------------------------------------------------------------------
export function makeFallbackSnapshot(
  moduleId: string,
  ctaRoute: string,
  iconName: string,
  ariaLabel: string,
): PreviewSnapshot {
  return {
    primaryMetric: { label: '', value: '' },
    ctaLabel: `Open ${moduleId}`,
    ctaRoute,
    iconName,
    ariaLabel,
    bgVariant: 'neutral',
    errorState: {
      message: 'Tap to open',
      recoveryRoute: ctaRoute,
    },
    computedAt: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Re-export the legacy types so consumers can `import from '../modules/types'`
// and get everything in one place.
// ---------------------------------------------------------------------------
export type { AweniContext, AweniModule, Lang };
